/**
 * The grammar of a library's folder paths, as the backend states it
 * (`external.api.atlas/src/modules/library/folderPath.ts`). Mirrored rather than trusted to the
 * server alone because the client builds paths itself — a breadcrumb, a folder dropped from
 * Finder, a rename's new location — and a path the backend would normalize differently names a
 * folder the client does not show. Keep the limits in step with that file.
 *
 * The client must never be stricter than the backend: `folderEntryPath` throws on a listed folder
 * these limits refuse, which fails the whole library listing. So a limit is relaxed here first and
 * in the backend only once this client has shipped, and tightened in the backend first.
 *
 * Pure: no React, no stores, so its `npx tsx` test runs without a bundler.
 */

/**
 * Where something sits in a library's folder tree: `'/'` for the library root, otherwise the chain
 * of folder names from it, e.g. `'/Trips/Lisbon'`. Never ends in `'/'`, never holds an empty
 * segment, and compares exactly — `'/Photos'` and `'/photos'` are two folders.
 */
export type FolderPath = string & { __FOLDER_PATH__: void };

export const FOLDER_ROOT_PATH = '/' as FolderPath;

export const FOLDER_NAME_MAX_LENGTH = 128;
export const FOLDER_DEPTH_MAX = 16;
export const FOLDER_PATH_MAX_LENGTH = 512;

/** A folder of a library's tree, as `GET /library/{id}/files` lists it. */
export type LibraryFolderEntry = {
  name: string;
  /** The folder holding this one; `'/'` for a top-level folder. */
  parent: FolderPath;
  /** Opaque to the API; one of `FOLDER_COLORS` when this client wrote it. */
  color?: string;
};

export const isFolderName = (name: string) =>
  name.length > 0 &&
  name.length <= FOLDER_NAME_MAX_LENGTH &&
  name !== '.' &&
  name !== '..' &&
  // eslint-disable-next-line no-control-regex
  !/[/\u0000-\u001f\u007f]/.test(name);

/**
 * Reads a string as a folder path, normalizing the spellings of one place (`'/A//B/'`,
 * `'/A/ B '`) the way the backend does. Null when no reading of it is a valid path.
 */
export const parseFolderPath = (raw: string): FolderPath | null => {
  const segments = raw
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  if (segments.length > FOLDER_DEPTH_MAX || !segments.every(isFolderName)) {
    return null;
  }

  const path = segments.length === 0 ? FOLDER_ROOT_PATH : (`/${segments.join('/')}` as FolderPath);
  return path.length <= FOLDER_PATH_MAX_LENGTH ? path : null;
};

/** The path of the folder `name` inside `parent`, or null when the two do not make a valid path. */
export const joinFolderPath = (parent: FolderPath, name: string): FolderPath | null =>
  isFolderName(name.trim()) ? parseFolderPath(`${parent}/${name}`) : null;

export const folderEntryPath = (folder: LibraryFolderEntry): FolderPath => {
  const path = joinFolderPath(folder.parent, folder.name);
  if (!path) {
    // The backend only stores entries its own parser accepted, and this one mirrors it.
    throw new Error(`Library folder does not name a valid path: ${JSON.stringify(folder)}`);
  }
  return path;
};

/** The folder holding `path`; the root is its own parent. */
export const parentFolderPath = (path: FolderPath): FolderPath => {
  const cut = path.lastIndexOf('/');
  return cut <= 0 ? FOLDER_ROOT_PATH : (path.slice(0, cut) as FolderPath);
};

/** The last segment of `path`; empty for the root, which has no name of its own. */
export const folderPathName = (path: FolderPath): string => path.slice(path.lastIndexOf('/') + 1);

/** Every folder from the root down to `path`, both included: `/A/B` → `['/', '/A', '/A/B']`. */
export const folderPathAncestry = (path: FolderPath): FolderPath[] => {
  if (path === FOLDER_ROOT_PATH) return [FOLDER_ROOT_PATH];
  const segments = path.split('/').slice(1);
  return [
    FOLDER_ROOT_PATH,
    ...segments.map((_, i) => `/${segments.slice(0, i + 1).join('/')}` as FolderPath),
  ];
};

/**
 * `path` as the breadcrumb every tooltip and label shows, the root named `rootName`:
 * `/A/B` → "Library › A › B".
 */
export const folderTrail = (path: FolderPath, rootName: string): string =>
  folderPathAncestry(path)
    .map((above) => (above === FOLDER_ROOT_PATH ? rootName : folderPathName(above)))
    .join(' › ');

export const folderPathDepth = (path: FolderPath): number =>
  path === FOLDER_ROOT_PATH ? 0 : path.split('/').length - 1;

/** Whether `path` sits under `ancestor`. A path is not its own descendant; everything is under the root. */
export const isDescendantFolderPath = (path: FolderPath, ancestor: FolderPath): boolean =>
  ancestor === FOLDER_ROOT_PATH ? path !== FOLDER_ROOT_PATH : path.startsWith(`${ancestor}/`);

export const isSameOrDescendantFolderPath = (path: FolderPath, ancestor: FolderPath): boolean =>
  path === ancestor || isDescendantFolderPath(path, ancestor);

/**
 * `path` with its `from` prefix rewritten to `to` — how everything under a folder follows it when
 * it is renamed or moved. Returns `path` untouched when it is neither `from` nor under it.
 */
export const repathFolderPath = (
  path: FolderPath,
  from: FolderPath,
  to: FolderPath,
): FolderPath => {
  if (!isSameOrDescendantFolderPath(path, from)) {
    return path;
  }
  const below = from === FOLDER_ROOT_PATH ? path : path.slice(from.length);
  const repathed = parseFolderPath(`${to}/${below}`);
  if (!repathed) {
    // The backend refuses a move that would push anything under it past the limits
    // (`library.folders.path_too_long`), so a successful move never lands here.
    throw new Error(`Repathing ${path} from ${from} to ${to} leaves the path limits`);
  }
  return repathed;
};

/**
 * The folder a media is shown in, given the folders that exist. A media names its folder by path,
 * and a delete interrupted between its two steps leaves media under a path the tree no longer
 * holds; those surface in the nearest folder that does exist rather than in no listing at all.
 */
export const resolveFolderPath = (
  path: FolderPath | undefined,
  existing: ReadonlySet<FolderPath>,
): FolderPath => {
  let candidate = path ?? FOLDER_ROOT_PATH;
  while (candidate !== FOLDER_ROOT_PATH && !existing.has(candidate)) {
    candidate = parentFolderPath(candidate);
  }
  return candidate;
};

/**
 * The palette a folder can be tinted with. Stored on the folder as the id (the API treats colour
 * as opaque), so a palette change restyles existing folders instead of stranding hex values.
 */
export const FOLDER_COLORS = [
  { id: 'violet', label: 'Violet', value: '#a78bfa' },
  { id: 'blue', label: 'Blue', value: '#60a5fa' },
  { id: 'teal', label: 'Teal', value: '#2dd4bf' },
  { id: 'green', label: 'Green', value: '#4ade80' },
  { id: 'yellow', label: 'Yellow', value: '#facc15' },
  { id: 'orange', label: 'Orange', value: '#fb923c' },
  { id: 'red', label: 'Red', value: '#f87171' },
  { id: 'pink', label: 'Pink', value: '#f472b6' },
] as const;

export type FolderColorId = (typeof FOLDER_COLORS)[number]['id'];

export const DEFAULT_FOLDER_COLOR = '#c1c1c2';

/** The CSS colour for a stored folder colour; a value this client did not write reads as untinted. */
export const folderColorValue = (color: string | undefined): string =>
  FOLDER_COLORS.find((c) => c.id === color)?.value ?? DEFAULT_FOLDER_COLOR;

/**
 * The folder `names` lead to from `base`, one level per name — where a file dropped inside
 * nested directories is filed. A name the grammar refuses, or a level past the depth limit,
 * stops the descent there: the file is filed in the deepest folder that can exist rather than
 * refused, since the names come from the user's disk and not from anything they typed here.
 */
export const descendFolderPath = (base: FolderPath, names: string[]): FolderPath => {
  let path = base;
  for (const name of names) {
    const next = joinFolderPath(path, name);
    if (!next) break;
    path = next;
  }
  return path;
};
